import React, { useRef, useMemo, useState } from 'react';
import { useFrame } from '@react-three/fiber';
import { Mesh, TextureLoader, Vector3, MeshStandardMaterial, DoubleSide, Color } from 'three';
import { Html } from '@react-three/drei';
import { useLanguage } from '../i18n/LanguageContext';

// Custom hook to create grid texture - exatamente igual ao do componente Spaceship original
const useGridTexture = () => {
  return useMemo(() => {
    const canvas = document.createElement('canvas')
    canvas.width = 200
    canvas.height = 200
    const ctx = canvas.getContext('2d')!

    // Set background
    const gradient = ctx.createLinearGradient(0, 0, 200, 200)
    gradient.addColorStop(0, '#00FF00') // Verde
    gradient.addColorStop(1, '#FF00FF') // Roxo
    ctx.fillStyle = gradient
    ctx.fillRect(0, 0, 200, 200)

    // Draw grid
    ctx.strokeStyle = '#000000' // Black grid lines
    ctx.lineWidth = 2

    // Create nodes and connections
    const nodes: Array<{x: number, y: number}> = []
    const cols = 8
    const rows = 10

    // Generate nodes with slight randomization
    for (let x = 0; x <= cols; x++) {
      for (let y = 0; y <= rows; y++) {
        const px = (x / cols) * 200 + (Math.random() * 20 - 10)
        const py = (y / rows) * 200 + (Math.random() * 20 - 10)
        nodes.push({ x: px, y: py })

        // Draw node
        ctx.beginPath()
        ctx.arc(px, py, 2, 0, Math.PI * 2)
        ctx.fillStyle = '#FFFFFF'
        ctx.fill()
      }
    }

    // Connect nearby nodes
    for (let i = 0; i < nodes.length; i++) {
      for (let j = i + 1; j < nodes.length; j++) {
        const dx = nodes[i].x - nodes[j].x
        const dy = nodes[i].y - nodes[j].y
        const distance = Math.sqrt(dx * dx + dy * dy)

        if (distance < 50) {
          ctx.beginPath()
          ctx.moveTo(nodes[i].x, nodes[i].y)
          ctx.lineTo(nodes[j].x, nodes[j].y)
          ctx.stroke()
        }
      }
    }

    return new TextureLoader().load(canvas.toDataURL())
  }, [])
};

interface FixedSpaceshipWithSpotlightProps {
  position: [number, number, number];
  mintUrl: string;
  marketplaceUrl: string;
}

const FixedSpaceshipWithSpotlight: React.FC<FixedSpaceshipWithSpotlightProps> = ({
  position,
  mintUrl,
  marketplaceUrl
}) => {
  const shipRef = useRef<Mesh>(null);
  const gridTexture = useGridTexture();
  const [showPopup, setShowPopup] = useState(false);
  const { language } = useLanguage();

  // Materials - exatamente iguais aos do componente Spaceship original
  const bodyMaterial = useMemo(() => {
    const material = new MeshStandardMaterial({
      map: gridTexture,
      metalness: 0.7,
      roughness: 0.2,
      side: DoubleSide
    })
    return material
  }, [gridTexture])

  const domeMaterial = useMemo(() =>
    new MeshStandardMaterial({
      transparent: true,
      opacity: 0.4,  // Mais transparente
      metalness: 0.1, // Menos metálico para parecer mais vidro
      roughness: 0.05, // Mais liso para refletir melhor
      color: new Color('#FFA500'),
      emissive: new Color('#FFA500'),
      emissiveIntensity: 0.3, // Mais brilho
      envMapIntensity: 1.5 // Aumenta reflexões
    }), []
  )

  const legMaterial = useMemo(() =>
    new MeshStandardMaterial({
      color: new Color('#8B4513'),
      metalness: 0.6,
      roughness: 0.3
    }), []
  )

  const propulsorMaterial = useMemo(() =>
    new MeshStandardMaterial({
      color: new Color('#000000'),
      metalness: 0.9,
      roughness: 0.1
    }), []
  );

  // Continuous rotation animation
  useFrame(({ clock }) => {
    if (shipRef.current) {
      // Rotate the spaceship around its Y axis
      shipRef.current.rotation.y = clock.getElapsedTime() * 0.5;
    }
  });

  // Handle click event
  const handleClick = (e: any) => {
    e.stopPropagation();
    setShowPopup(!showPopup);
  };

  // Open URL in a new tab
  const openUrl = (url: string) => {
    window.open(url, '_blank');
  };

  return (
    <group position={position}>
      {/* Luz pontual para destacar o spaceship */}
      <pointLight position={[0, 8, 0]} intensity={3} distance={30} color="#FFA500" />

      {/* Luz adicional para destacar o spaceship - agora parte do mesmo componente */}
      <spotLight
        position={[0, 20, 0]}
        angle={0.3}
        penumbra={0.5}
        intensity={2}
        color="#FFFFFF"
        castShadow
        target-position={[0, 0, 0]}
      />

      <mesh
        ref={shipRef}
        onClick={handleClick}
        onPointerOver={() => document.body.style.cursor = 'pointer'}
        onPointerOut={() => document.body.style.cursor = 'auto'}
        scale={[8, 8, 8]} // Aumentar o tamanho em 8x
      >
        {/* Main body (oval with grid pattern) */}
        <mesh material={bodyMaterial} scale={[1.0, 0.34, 1.0]}>
          <sphereGeometry args={[1, 32, 32]} />
        </mesh>

        {/* Transparent orange dome */}
        <mesh material={domeMaterial} position={[0, 0.26, 0]} scale={[0.40, 0.46, 0.40]}>
          <sphereGeometry args={[1, 32, 32]} />
        </mesh>

        {/* Support legs - apenas 2 pernas */}
        <group position={[0, -0.26, 0]}>
          {/* Left leg - posição ajustada */}
          <mesh material={legMaterial} position={[-0.4, 0, 0.1]}>
            <boxGeometry args={[0.06, 0.4, 0.06]} />
          </mesh>
          {/* Right leg - posição ajustada */}
          <mesh material={legMaterial} position={[0.4, 0, 0.1]}>
            <boxGeometry args={[0.06, 0.4, 0.06]} />
          </mesh>
        </group>

        {/* Side propulsors (closer together) */}
        <group position={[0, -0.14, -0.46]}>
          {/* Left propulsor */}
          <mesh material={propulsorMaterial} position={[-0.2, 0, 0]} rotation={[0, 0, Math.PI / 2]}>
            <cylinderGeometry args={[0.1, 0.1, 0.26, 16]} />
          </mesh>
          {/* Right propulsor */}
          <mesh material={propulsorMaterial} position={[0.2, 0, 0]} rotation={[0, 0, Math.PI / 2]}>
            <cylinderGeometry args={[0.1, 0.1, 0.26, 16]} />
          </mesh>
        </group>
      </mesh>

      {/* A seta indicativa foi movida para um componente separado (FixedArrow) */}

      {/* Popup with links - design mais elegante */}
      {showPopup && (
        <Html position={[0, -10, 0]}>
          <div style={{
            background: 'rgba(10, 10, 20, 0.75)',
            color: 'white',
            padding: '25px',
            borderRadius: '12px',
            width: '340px',
            textAlign: 'center',
            backdropFilter: 'blur(15px)',
            border: '1px solid rgba(255, 215, 0, 0.3)',
            transform: 'translate(-50%, 0)',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.5), 0 0 20px rgba(255, 215, 0, 0.2)',
            zIndex: 1000,
            position: 'relative',
            overflow: 'hidden'
          }}>
            {/* Efeito de brilho no fundo */}
            <div style={{
              position: 'absolute',
              top: '-50%',
              left: '-50%',
              width: '200%',
              height: '200%',
              background: 'radial-gradient(circle, rgba(255, 215, 0, 0.1) 0%, rgba(0, 0, 0, 0) 70%)',
              pointerEvents: 'none',
              zIndex: -1
            }}></div>

            <h2 style={{
              margin: '0 0 20px 0',
              color: '#FFD700',
              fontSize: '16px',
              fontWeight: 'bold',
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)',
              letterSpacing: '0.5px',
              position: 'relative',
              display: 'inline-block',
              whiteSpace: 'nowrap',
              maxWidth: '100%'
            }}>
              {language === 'en' ? 'MULTIVERSO PASS TO FLY' : 'MULTIVERSO PASS PARA VOAR'}
              <div style={{
                position: 'absolute',
                bottom: '-8px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '60px',
                height: '2px',
                background: 'linear-gradient(90deg, rgba(255,215,0,0) 0%, rgba(255,215,0,1) 50%, rgba(255,215,0,0) 100%)'
              }}></div>
            </h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <button
                onClick={() => openUrl(mintUrl)}
                style={{
                  background: 'linear-gradient(135deg, rgba(255, 140, 0, 0.8), rgba(255, 165, 0, 0.8))',
                  border: '1px solid rgba(255, 215, 0, 0.5)',
                  borderRadius: '8px',
                  padding: '12px 0',
                  color: 'white',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '16px',
                  boxShadow: '0 4px 10px rgba(0, 0, 0, 0.3)',
                  transition: 'all 0.2s ease',
                  position: 'relative',
                  overflow: 'hidden'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, rgba(255, 165, 0, 0.9), rgba(255, 190, 0, 0.9))';
                  e.currentTarget.style.boxShadow = '0 6px 15px rgba(255, 140, 0, 0.3)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, rgba(255, 140, 0, 0.8), rgba(255, 165, 0, 0.8))';
                  e.currentTarget.style.boxShadow = '0 4px 10px rgba(0, 0, 0, 0.3)';
                }}
              >
                Mint
              </button>

              <button
                onClick={() => openUrl(marketplaceUrl)}
                style={{
                  background: 'linear-gradient(135deg, rgba(138, 43, 226, 0.8), rgba(147, 112, 219, 0.8))',
                  border: '1px solid rgba(147, 112, 219, 0.5)',
                  borderRadius: '8px',
                  padding: '12px 0',
                  color: 'white',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '16px',
                  boxShadow: '0 4px 10px rgba(0, 0, 0, 0.3)',
                  transition: 'all 0.2s ease',
                  position: 'relative',
                  overflow: 'hidden'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, rgba(147, 112, 219, 0.9), rgba(153, 123, 235, 0.9))';
                  e.currentTarget.style.boxShadow = '0 6px 15px rgba(138, 43, 226, 0.3)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, rgba(138, 43, 226, 0.8), rgba(147, 112, 219, 0.8))';
                  e.currentTarget.style.boxShadow = '0 4px 10px rgba(0, 0, 0, 0.3)';
                }}
              >
                Magic Eden
              </button>
            </div>
          </div>
        </Html>
      )}
    </group>
  );
};

export default FixedSpaceshipWithSpotlight;
